import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';
import { CopyButton } from '@/components/ui/copy-button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  formatCurrency,
  formatDateTime,
  formatPhone,
  formatRelative,
} from '@/lib/format';
import { paymentLabel } from './order-enums';
import type { Order } from './types';

/**
 * The expanded row: General, and Details for anything that is not a pickup.
 *
 * Same information as the original's expanded row — customer, pickup name,
 * dates, payment, totals, transfers, and the POS block for in-store orders —
 * laid out as labelled definition lists instead of four columns of `<br />`
 * separated template strings, so it reflows on a phone.
 *
 * Items and history are deliberately absent: the original keeps items behind a
 * hidden column and history behind its own Logs button, and neither belongs in
 * the row body.
 */

const TYPE_PICKUP = 0;
const TYPE_IN_STORE = 5;

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <h4 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
        {title}
      </h4>
      <dl className="space-y-1.5 text-sm">{children}</dl>
    </section>
  );
}

function Row({
  label,
  children,
  emphasis,
}: {
  label: string;
  children: React.ReactNode;
  emphasis?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-muted-foreground shrink-0">{label}</dt>
      <dd
        className={
          emphasis
            ? 'text-right font-semibold tabular-nums'
            : 'text-right tabular-nums'
        }
      >
        {children}
      </dd>
    </div>
  );
}

/** Whole hours between placing the order and the requested service time. */
function leadTimeHours(order: Order): number | null {
  if (!order.desiredDateTime || !order.actualTime) return null;
  const desired = new Date(`${order.desiredDateTime}Z`).getTime();
  const actual = new Date(`${order.actualTime}Z`).getTime();
  if (Number.isNaN(desired) || Number.isNaN(actual)) return null;
  return Math.round((desired - actual) / 3_600_000);
}

function GeneralTab({ order }: { order: Order }) {
  const { t } = useTranslation();
  const lead = leadTimeHours(order);

  return (
    <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
      <div className="space-y-6">
        <Section title={t('orders.customer')}>
          <div className="group flex items-center justify-between gap-2">
            <span>{order.customerName || '—'}</span>
            {order.customerName && <CopyButton value={order.customerName} />}
          </div>
          <div className="group flex items-center justify-between gap-2">
            <span>{formatPhone(order.customerPhone)}</span>
            {order.customerPhone && <CopyButton value={order.customerPhone} />}
          </div>
        </Section>

        {order.pickUpName && (
          <Section title={t('orders.pickupName')}>
            <p>{order.pickUpName}</p>
          </Section>
        )}
      </div>

      <Section title={t('orders.dates')}>
        <Row label={t('orders.created')}>
          <span className="block">{formatRelative(order.actualTime)}</span>
          <span className="text-muted-foreground block text-xs">
            {formatDateTime(order.actualTime)}
          </span>
        </Row>
        <Row label={t('orders.expected')}>
          <span className="block">{formatRelative(order.desiredDateTime)}</span>
          <span className="text-muted-foreground block text-xs">
            {formatDateTime(order.desiredDateTime)}
          </span>
        </Row>
        <Row label={t('orders.leadTime')}>
          {lead === null ? '—' : t('orders.hours', { count: lead })}
        </Row>
        <Row label={t('orders.dispatched')}>
          {order.dispatchDateTime ? formatDateTime(order.dispatchDateTime) : t('common.no')}
        </Row>
      </Section>

      <div className="space-y-6">
        <Section title={t('orders.payment')}>
          <Row label={t('orders.method')}>{paymentLabel(order.paymentType)}</Row>
          {order.giftCardCode && (
            <Row label={t('orders.giftCard')}>
              <span className="font-mono text-xs">{order.giftCardCode}</span>
            </Row>
          )}
          <Row label={t('orders.card')}>{order.nickName || '—'}</Row>
          <Row label={t('orders.cardHolder')}>
            {order.cardholderName || '—'}
          </Row>
          <Row label={t('orders.authorized')}>
            {order.hasBeenAuthed ? t('common.yes') : t('common.no')}
          </Row>
          <Row label={t('orders.charged')}>
            {order.hasBeenCharged ? t('common.yes') : t('common.no')}
          </Row>
          <Row label={t('orders.ivuControl')}>
            {order.ivuControlNumber || t('common.notAvailable')}
          </Row>
        </Section>

        <Section title={t('orders.transfers')}>
          <Row label={t('orders.transferPending')}>
            {formatCurrency(order.estimatedStoreTransferTotal)}
          </Row>
          <Row label={t('orders.transferPaidOut')}>
            {order.transferCompleted ? t('common.yes') : t('common.no')}
          </Row>
          <Row label={t('orders.transferPaidAmount')}>
            {formatCurrency(order.transferTotal)}
          </Row>
        </Section>
      </div>

      <div className="space-y-6">
        <Section title={t('orders.totals')}>
          <Row label={t('orders.subtotal')}>
            {formatCurrency(order.productsTotal)}
          </Row>
          {/* The original printed stateTax under the municipal label and
              municipalTax under the state label — the two were swapped. */}
          <Row label={t('orders.municipalTax')}>
            {formatCurrency(order.municipalTax)}
          </Row>
          <Row label={t('orders.stateTax')}>
            {formatCurrency(order.stateTax)}
          </Row>
          <Row label={t('orders.serviceFee')}>
            {formatCurrency(order.processingFee)}
          </Row>
          <Row label={t('orders.deliveryFee')}>
            {formatCurrency(order.deliveryTotal)}
          </Row>
          <div className="border-t pt-1.5">
            <Row label={t('orders.total')} emphasis>
              {formatCurrency(order.orderTotal)}
            </Row>
          </div>
          <Row label={t('orders.discount')}>
            {formatCurrency(order.discountAmount)}
          </Row>
          <Row label={t('orders.return')}>
            {formatCurrency(order.returnTotal)}
          </Row>
        </Section>

        {order.type === TYPE_IN_STORE && (
          <Section title="POS">
            <Row label={t('orders.posLane')}>{order.posLaneTag || '—'}</Row>
            <Row label={t('orders.terminalId')}>
              {order.posTerminalId || '—'}
            </Row>
            <Row label={t('orders.version')}>{order.posVersion || '—'}</Row>
            <Row label={t('orders.deviceName')}>
              {order.posDeviceName || '—'}
            </Row>
            <Row label={t('orders.deviceModel')}>
              {order.posDeviceModel || '—'}
            </Row>
            <Row label={t('orders.deviceManufacturer')}>
              {order.posDeviceManufacturer || '—'}
            </Row>
          </Section>
        )}
      </div>
    </div>
  );
}

function DetailsTab({ order }: { order: Order }) {
  const { t } = useTranslation();
  const location = order.customerLocation;

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Section title={t('orders.delivery')}>
        <Row label={t('orders.zone')}>{order.deliveryZoneName || '—'}</Row>
        <Row label={t('orders.driver')}>{order.driverName || '—'}</Row>
        {order.driverPhone && (
          <Row label={t('orders.driverPhone')}>
            {formatPhone(order.driverPhone)}
          </Row>
        )}
        {order.driverInstructions && (
          <Row label={t('orders.driverInstructions')}>
            <span className="text-left">{order.driverInstructions}</span>
          </Row>
        )}
      </Section>

      <Section title={t('orders.deliveryAddress')}>
        {location ? (
          <div className="flex gap-2 text-sm">
            <MapPin className="text-muted-foreground mt-0.5 size-4 shrink-0" />
            <div>
              {location.name && <p className="font-medium">{location.name}</p>}
              <p>{location.addressLine}</p>
              <p className="text-muted-foreground">
                {[location.city, location.zipcode].filter(Boolean).join(', ')}
              </p>
              {location.instructions && (
                <p className="text-muted-foreground mt-1 text-xs italic">
                  {location.instructions}
                </p>
              )}
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">
            {t('orders.noAddress')}
          </p>
        )}
      </Section>
    </div>
  );
}

export function OrderDetail({ order }: { order: Order }) {
  const { t } = useTranslation();
  // The original hid the Details tab for pickups, which have no delivery leg.
  const showDetails = order.type !== TYPE_PICKUP;

  if (!showDetails) {
    return (
      <div className="pt-1">
        <GeneralTab order={order} />
      </div>
    );
  }

  return (
    <Tabs defaultValue="general">
      <TabsList className="sm:w-auto">
        <TabsTrigger value="general">{t('orders.general')}</TabsTrigger>
        <TabsTrigger value="details">{t('orders.detailsTab')}</TabsTrigger>
      </TabsList>
      <TabsContent value="general">
        <GeneralTab order={order} />
      </TabsContent>
      <TabsContent value="details">
        <DetailsTab order={order} />
      </TabsContent>
    </Tabs>
  );
}
